<template>
  <div v-if="hasSale && discountValue" class="sale-banner">
    <LightningChargeFill class="w-5 h-5 text-orange-600 flex-shrink-0" />
    <span class="text-sm font-medium text-orange-900">
      {{ saleMessage }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import type { EventDescription } from '~/composables/types/event'
import LightningChargeFill from '~/assets/icons/lightning-charge-fill.svg?component'
import { formatPriceAmount } from '~/utils/currencyHelpers'
import { useMergedCalendarDayMap } from '~/composables/useEventBuyBoxDatePicker'

interface Props {
  hasSale: boolean
  eventData: EventDescription
}

const props = defineProps<Props>()

const { t, locale } = useI18n()

// Calendar days loaded by the date picker (shared state)
const calendarDayMap = useMergedCalendarDayMap(
  eventGetters.getId(props.eventData)
)

// Largest percentage off among loaded calendar dates, for date-specific
// promotions that come without an event-wide activePromotion
const maxDatesDiscountPercent = computed(() => {
  const percents = Object.values(calendarDayMap.value)
    .flatMap((calendarDay) => calendarDay.dates || [])
    .filter(eventDateGetters.getHasPromotion)
    .map(
      (date) =>
        (1 -
          Number(eventDateGetters.getPriceAmount(date)) /
            Number(date.product.original_price.amount)) *
        100
    )
  return percents.length ? Math.round(Math.max(...percents)) : undefined
})

// "20%" or "20 €": the event's active promotion, else the best date discount
const discountValue = computed(() => {
  const promotion = props.eventData?.activePromotion?.data
  if (!promotion || promotion.discount === undefined) {
    const percent = maxDatesDiscountPercent.value
    return percent ? `${percent}%` : undefined
  }

  if (promotion.discount_type === 'PERCENTAGE') {
    return `${Math.round(promotion.discount)}%`
  }
  if (promotion.discount_type === 'FIXED_VALUE') {
    // discount is in cents
    return formatPriceAmount(promotion.discount, locale.value, 'EUR')
  }
  return undefined
})

const saleMessage = computed(() =>
  t('product.sale.bannerUpTo', { value: discountValue.value })
)
</script>

<style lang="postcss" scoped>
.sale-banner {
  @apply p-3 flex items-center justify-center gap-2 mb-1;
  background-color: #fff7ed;
  color: #e07050;
}
</style>
